import { useCallback, useRef, useState } from 'react'

/**
 * A hook that manages dialog state with promise-based API.
 * This allows you to await dialog results instead of using callbacks.
 *
 * @example
 * const confirmDialog = usePromiseDialog()
 *
 * // In your component
 * const handleClick = async () => {
 *   const result = await confirmDialog.show({ userId: 123 })
 *   if (result.confirmed) {
 *     console.log('User confirmed with data:', result.data)
 *   } else {
 *     console.log('User cancelled')
 *   }
 * }
 *
 * // In your JSX
 * <Dialog open={confirmDialog.isOpen}>
 *   <button onClick={() => confirmDialog.resolve({ confirmed: true, data: someData })}>
 *     Confirm
 *   </button>
 *   <button onClick={() => confirmDialog.reject()}>
 *     Cancel
 *   </button>
 * </Dialog>
 */
export function usePromiseDialog() {
  const [isOpen, setIsOpen] = useState(false)
  const [dialogData, setDialogData] = useState(null)
  const resolverRef = useRef(null)

  /**
   * Opens the dialog and returns a promise that resolves when user takes action
   * @param {any} data - Optional data to pass to the dialog
   * @returns {Promise} Promise that resolves with result or rejects if cancelled
   */
  const show = useCallback((data = null) => {
    setDialogData(data)
    setIsOpen(true)

    return new Promise((resolve, reject) => {
      resolverRef.current = { resolve, reject }
    })
  }, [])

  /**
   * Resolves the promise with result and closes dialog
   * @param {any} result - The result to resolve with
   */
  const resolve = useCallback((result) => {
    if (resolverRef.current) {
      resolverRef.current.resolve(result)
      resolverRef.current = null
    }
    setIsOpen(false)
    setDialogData(null)
  }, [])

  /**
   * Rejects the promise and closes dialog
   * @param {any} reason - Optional rejection reason
   */
  const reject = useCallback((reason = 'cancelled') => {
    if (resolverRef.current) {
      resolverRef.current.reject(reason)
      resolverRef.current = null
    }
    setIsOpen(false)
    setDialogData(null)
  }, [])

  /**
   * Closes the dialog and rejects the promise
   */
  const close = useCallback(() => {
    reject('closed')
  }, [reject])

  return {
    isOpen,
    dialogData,
    show,
    resolve,
    reject,
    close,
  }
}
